import type { FC, ReactNode } from 'react';
import React, { useMemo } from 'react';

import { EXTERNAL_SHARE, SHAREABLE_LINK } from '@confluence/named-routes/entry-points/routes.ts';
import { expVal } from '@confluence/feature-experiments';

import { useWithUserTypes } from './useWithUserTypes.ts';
import { ExternalShareContext } from './ExternalShareContext.tsx';

type ExternalShareContextProviderProps = {
	path: string;
	children?: ReactNode;
	shareToken?: string;
};

export const ExternalShareContextProvider: FC<ExternalShareContextProviderProps> = ({
	children,
	path,
	shareToken,
}) => {
	const isExternalShareRequest = !!EXTERNAL_SHARE.match(path);
	const isShareableLinkRequest =
		!!SHAREABLE_LINK.match(path) && expVal('shareable_links_m0', 'isEnabled', false);
	const { userType } = useWithUserTypes(isExternalShareRequest || isShareableLinkRequest);

	// Extract shareToken from path if it's an external share request
	const extractedShareToken = useMemo(() => {
		if (isExternalShareRequest) {
			const match = EXTERNAL_SHARE.match(path);
			return match?.params?.externalToken || undefined;
		} else if (isShareableLinkRequest) {
			const match = SHAREABLE_LINK.match(path);
			return match?.params?.shareToken || undefined;
		}
		return undefined;
	}, [isExternalShareRequest, path, isShareableLinkRequest]);

	// Use provided shareToken if available, otherwise use extracted one
	const resolvedShareToken = shareToken || extractedShareToken;

	const externalShareContextValue = useMemo(
		() => ({
			isExternalShareRequest: isExternalShareRequest || isShareableLinkRequest,
			userType,
			shareToken: resolvedShareToken,
			isShareableLinkRequest,
		}),
		[isExternalShareRequest, userType, resolvedShareToken, isShareableLinkRequest],
	);

	return (
		<ExternalShareContext.Provider value={externalShareContextValue}>
			{children}
		</ExternalShareContext.Provider>
	);
};
